import { FreeMode, Navigation, Pagination, Scrollbar } from "swiper/modules";
import { Swiper } from "swiper/react";
import { clsx } from "clsx";
import {
  PaginationOptions,
  ScrollbarOptions,
  Swiper as SwiperType,
  SwiperOptions,
} from "swiper/types";
import IconArrowRight from "@/components/icons/IconArrowRight";
import { useRef, useState } from "react";

export default function SwiperCarousel({
  className,
  children,
  breakpoints,
  slidesPerView = 1,
  centeredSlides = false,
  freemode = false,
  initialSlide = 0,
  paginationClassName,
  scrollbar,
  pagination,
}: React.PropsWithChildren<{
  className?: string;
  children: React.ReactNode;
  slidesPerView?: number | "auto" | undefined;
  centeredSlides?: boolean;
  breakpoints?: {
    [width: number]: SwiperOptions;
    [ratio: string]: SwiperOptions;
  };
  freemode?: boolean;
  initialSlide?: number;
  paginationClassName?: string;
  scrollbar?: ScrollbarOptions | boolean;
  pagination?: PaginationOptions | boolean;
}>) {
  const swiperRef = useRef<null | SwiperType>(null);
  const [realIndex, setIndex] = useState(initialSlide);
  const [isEnd, setIsEnd] = useState(false);

  return (
    <div className="relative">
      <div className="relative z-10 overflow-hidden">
        <div
          className={clsx(
            "relative mx-5 max-w-screen-lg pr-7 lg:m-auto lg:pl-0 lg:pr-44",
            className,
          )}
        >
          <Swiper
            onBeforeInit={(swiper) => {
              swiperRef.current = swiper;
            }}
            onSwiper={(swiper) => {
              setIsEnd(swiper.isEnd);
              if (initialSlide > 0) {
                setTimeout(() => {
                  swiper.slideTo(initialSlide, 0, false);
                }, 50);
              }
            }}
            onSlideChange={(swiper) => {
              setIndex(swiper.realIndex);
              setIsEnd(swiper.isEnd);
            }}
            onBreakpoint={(swiper) => setIsEnd(swiper.isEnd)}
            onResize={(swiper) => setIsEnd(swiper.isEnd)}
            spaceBetween={20}
            style={{ position: "initial" }}
            className="overflow-visible pb-10 lg:pb-16"
            scrollbar={
              scrollbar ?? {
                hide: false,
              }
            }
            pagination={pagination ?? false}
            initialSlide={initialSlide}
            slidesPerView={slidesPerView}
            freeMode={freemode}
            centeredSlides={centeredSlides}
            breakpoints={
              breakpoints ?? {
                0: {
                  slidesPerView: 1,
                  spaceBetween: 20,
                  slidesOffsetAfter: -28,
                },
                1024: {
                  slidesPerView: 2,
                  spaceBetween: 30,
                  slidesOffsetAfter: -176,
                },
              }
            }
            modules={[Scrollbar, Navigation, FreeMode, Pagination]}
          >
            {children}
          </Swiper>
        </div>
      </div>
      <button
        type={"button"}
        aria-label={"Poprzedni"}
        disabled={realIndex == 0}
        onClick={() => swiperRef.current?.slidePrev()}
        className={clsx(
          "swiper-action-prev absolute left-5 top-1/2 z-10 -mt-5 h-20 w-20 -translate-y-1/2 rotate-180 items-center justify-center rounded-full bg-white lg:-mt-8",
          realIndex == 0 ? "hidden" : "hidden lg:flex",
          paginationClassName,
        )}
      >
        <IconArrowRight className="w-8" />
      </button>
      <button
        type={"button"}
        aria-label={"Następny"}
        disabled={isEnd}
        onClick={() => swiperRef.current?.slideNext()}
        className={clsx(
          "swiper-action-next absolute right-5 top-1/2 z-10 -mt-5 h-20 w-20 -translate-y-1/2 items-center justify-center rounded-full bg-white lg:-mt-8",
          isEnd ? "hidden" : "hidden lg:flex",
          paginationClassName,
        )}
      >
        <IconArrowRight className="w-8" />
      </button>
    </div>
  );
}
